import { tenantApi as api } from '../config/api'
const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    listTenantHierarchies: build.query<ListTenantHierarchiesApiResponse, ListTenantHierarchiesApiArg>({
      query: (queryArg) => ({
        url: `/tenant-hierarchies`,
        params: {
          filter_by: queryArg.filterBy,
          query: queryArg.query,
          page: queryArg.page,
          size: queryArg.size,
        },
      }),
    }),
    listEligibleParents: build.query<ListEligibleParentsApiResponse, ListEligibleParentsApiArg>({
      query: () => ({ url: `/tenant-hierarchies/eligible-parents` }),
    }),
    listTenantChildren: build.query<ListTenantChildrenApiResponse, ListTenantChildrenApiArg>({
      query: (queryArg) => ({ url: `/tenants/${queryArg.tenantId}/children` }),
    }),
    createTenantRelationships: build.mutation<CreateTenantRelationshipsApiResponse, CreateTenantRelationshipsApiArg>({
      query: (queryArg) => ({
        url: `/tenants/${queryArg.tenantId}/children`,
        method: 'POST',
        body: queryArg.createTenantRelationshipsDto,
      }),
    }),
    archiveTenantRelationships: build.mutation<ArchiveTenantRelationshipsApiResponse, ArchiveTenantRelationshipsApiArg>(
      {
        query: (queryArg) => ({
          url: `/tenants/${queryArg.tenantId}/children/archive`,
          method: 'PUT',
          body: queryArg.archiveTenantRelationshipsDto,
        }),
      },
    ),
    getSubsidiariesOverview: build.query<GetSubsidiariesOverviewApiResponse, GetSubsidiariesOverviewApiArg>({
      query: (queryArg) => ({ url: `/tenants/${queryArg.tenantId}/subsidiaries/overview` }),
    }),
  }),
  overrideExisting: false,
})
export { injectedRtkApi as enhancedApi }
export type ListTenantHierarchiesApiResponse = /** status 200 Successfully retrieved tenant hierarchies. */
  | TenantHierarchyDto[]
  | PageTenantHierarchyDto
export type ListTenantHierarchiesApiArg = {
  filterBy?: TenantHierarchyFilter
  query?: string
  page?: number
  size?: number
}
export type ListEligibleParentsApiResponse =
  /** status 200 Successfully retrieved eligible parent tenants. */ TenantDto[]
export type ListEligibleParentsApiArg = void
export type ListTenantChildrenApiResponse = /** status 200 Successfully retrieved tenant children. */ TenantChildDto[]
export type ListTenantChildrenApiArg = {
  /** Tenant the request is scoped to. */
  tenantId: string
}
export type CreateTenantRelationshipsApiResponse =
  /** status 207 Multi-status response - created and invalid relationships. */ CreateTenantRelationshipsResponseDto
export type CreateTenantRelationshipsApiArg = {
  /** Tenant the request is scoped to. */
  tenantId: string
  createTenantRelationshipsDto: CreateTenantRelationshipsDto
}
export type ArchiveTenantRelationshipsApiResponse = unknown
export type ArchiveTenantRelationshipsApiArg = {
  /** Tenant the request is scoped to. */
  tenantId: string
  archiveTenantRelationshipsDto: ArchiveTenantRelationshipsDto
}
export type GetSubsidiariesOverviewApiResponse =
  /** status 200 Successfully retrieved subsidiaries overview. */ SubsidiariesOverviewDto
export type GetSubsidiariesOverviewApiArg = {
  /** Tenant the request is scoped to. */
  tenantId: string
}
export type TenantChildDto = {
  default_language_tag: string
  details: string
  id: string
  name: string
  salesforce_id: string
  subdomain: string
}
export type TenantParentDto = {
  default_language_tag: string
  details: string
  id: string
  name: string
  salesforce_id: string
  subdomain: string
}
export type TenantDto = {
  default_language_tag: string
  details: string
  id: string
  name: string
  salesforce_id: string
  subdomain: string
}
export type TenantType = 'standalone' | 'parent' | 'child'
export type TenantHierarchyDto = {
  children: TenantChildDto[]
  parent?: TenantParentDto
  tenant: TenantDto
  type: TenantType
}
export type PageTenantHierarchyDto = {
  items: TenantHierarchyDto[]
  page?: number
  pages?: number
  size?: number
  total?: number
}
export type ValidationError = {
  loc: (string | number)[]
  msg: string
  type: string
}
export type HttpValidationError = {
  detail?: ValidationError[]
}
export type TenantHierarchyFilter = 'all' | 'non_child' | 'parent' | 'standalone' | 'child'
export type CreatedRelationshipDto = {
  child_tenant: TenantDto
  created_at: string
  parent_tenant: TenantDto
}
export type ValidationStatus = 'valid' | 'invalid'
export type InvalidRelationshipDto = {
  child_id: string
  child_tenant?: TenantDto
  error_type: string
  parent_tenant: TenantDto
  reason: string
  status: ValidationStatus
}
export type CreateTenantRelationshipsResponseDto = {
  created_relationships: CreatedRelationshipDto[]
  invalid_relationships: InvalidRelationshipDto[]
}
export type CreateTenantRelationshipsDto = {
  children_ids: string[]
}
export type ArchiveTenantRelationshipsDto = {
  children_ids: string[]
}
export type LearningManagerDto = {
  email?: string
  name?: string
}
export type SubsidiaryOverviewDto = {
  id: string
  learning_managers?: LearningManagerDto[]
  name: string
  subdomain: string
  total_learners: number
  total_trainings: number
}
export type SubsidiariesOverviewDto = {
  subsidiaries: SubsidiaryOverviewDto[]
  total_learners_all: number
  total_subsidiaries: number
  total_trainings_all: number
}
export const {
  useListTenantHierarchiesQuery,
  useListEligibleParentsQuery,
  useListTenantChildrenQuery,
  useCreateTenantRelationshipsMutation,
  useArchiveTenantRelationshipsMutation,
  useGetSubsidiariesOverviewQuery,
} = injectedRtkApi
